import React from 'react';
import {
  NumberInputField,
  CheckboxInputField,
  TextareaInputField,
  TextInputField,
} from '../FormFields';
import { validatedWorkflowFields } from '../../validatedWorkflowFieldAtoms';
import { useWorkflowFormSubmit } from '../../useWorkflowFormSubmit';
import styles from './WorkflowPropertiesForm.module.scss';

/**
 * Workflow properties form component
 * Manages workflow configuration fields with validation
 */
export const WorkflowPropertiesForm: React.FC = () => {
  const { validateAllFields } = useWorkflowFormSubmit();
  const _onValidate = () => {
    const { errors, isValid } = validateAllFields();
    if (isValid) {
      console.log('All fields are valid!');
    } else {
      console.error('Validation errors:', errors);
    }
  };

  return (
    <div className={styles.formContainer}>
      <h2>Workflow Properties</h2>

      <div className={styles.formSection}>
        <h3>Basic Information</h3>

        <TextInputField
          fieldAtoms={validatedWorkflowFields.name}
          helperText="The database constraint for this property is NVARCHAR(255)."
          label="Orchestration Name"
          placeholder="Enter the orchestration name"
          required
        />

        <TextareaInputField
          fieldAtoms={validatedWorkflowFields.description}
          helperText='The Orchestration description should be a single sentence beginning with a verb, e.g. "Imports documents to Apollo using Sync Import Service". The database constraint for this property is NVARCHAR(1000).'
          label="Orchestration Description"
          placeholder="Describe the orchestration"
          rows={3}
        />

        <TextInputField
          fieldAtoms={validatedWorkflowFields.businessProduct}
          helperText="This field helps distinguish role-specific permissions in BU Admin. The database constraint for this property is NVARCHAR(300)."
          label="Business Product"
          placeholder="Enter the business product name"
        />

        <TextInputField
          fieldAtoms={validatedWorkflowFields.slave}
          helperText="Enter the CSB Worker name, e.g. enter apacri for csb-worker-apacri. The CSB UI uses this value to allow you to see which Orchestrations in a CSB Worker use specific Modules. The database constraint for this property is NVARCHAR(300)."
          label="Worker"
          placeholder="Enter the CSB Worker name"
          required
        />
      </div>

      <div className={styles.formSection}>
        <h3>Scheduling Configuration</h3>

        <NumberInputField
          fieldAtoms={validatedWorkflowFields.throttleRate}
          helperText="The Throttle Rate is the maximum number of product instances that can be executed simultaneously. The property value must be an integer value or no value."
          label="Throttle Rate"
          max={1000}
          min={1}
          placeholder="Enter an integer or leave blank"
          required
          step={1}
        />

        <TextInputField
          fieldAtoms={validatedWorkflowFields.cronTrigger}
          helperText="The Cron Trigger defines the timestamp when the product instance should be triggered. It should contain a valid Cron expression. The database constraint for this property is NVARCHAR(300)."
          label="Cron Trigger"
          placeholder="Enter a valid cron expression like 0 0 * * *"
        />

        <TextInputField
          fieldAtoms={validatedWorkflowFields.cronTimezone}
          helperText="This optional property allows you to capture the Timezone in which the product instance should be triggered. The database constraint for this property is NVARCHAR(300)."
          label="Cron Timezone"
          placeholder="Enter a valid timezone according to TimeZone.java"
        />
      </div>

      <div className={styles.formSection}>
        <h3>File Transfer Configuration</h3>

        <TextInputField
          fieldAtoms={validatedWorkflowFields.sftpDropzone}
          helperText="Name of the SFTP dropzone from where the orchestration is triggered when a file is dropped for processing. The database constraint for this property is NVARCHAR(300)."
          label="SFTP Dropzone"
          placeholder="Enter SFTP dropzone name"
        />

        <TextInputField
          fieldAtoms={validatedWorkflowFields.sftpPrivateKey}
          helperText="This is the SFTP private key that is used to authenticate a client when it connects to the SFTP server. The database constraint for this property is NVARCHAR(300)."
          label="SFTP Private Key"
          placeholder="Enter SFTP Private Key"
        />

        <TextInputField
          fieldAtoms={validatedWorkflowFields.ftpDropzone}
          helperText="Name of the FTP dropzone from where the orchestration is triggered when a file is dropped for processing. The database constraint for this property is NVARCHAR(300)."
          label="FTP Dropzone"
          placeholder="Enter FTP dropzone name"
        />

        <TextInputField
          fieldAtoms={validatedWorkflowFields.s3Dropzone}
          helperText="Name of the S3 dropzone from where the orchestration is triggered when a file is dropped for processing. The database constraint for this property is NVARCHAR(300)."
          label="S3 Dropzone"
          placeholder="Enter S3 dropzone name"
        />

        <TextInputField
          fieldAtoms={validatedWorkflowFields.s3AccessKey}
          helperText="This property stores the AWS S3 Access Key. The property's database constraint is NVARCHAR(50)."
          label="S3 Access Key"
          placeholder="Enter the AWS S3 Access Key"
        />

        <TextInputField
          fieldAtoms={validatedWorkflowFields.s3PrivateKey}
          helperText="This property stores the AWS S3 Private Key. The property's database constraint is NVARCHAR(50)."
          label="S3 Private Key"
          placeholder="Enter the AWS S3 Private Key"
        />
        <TextInputField
          fieldAtoms={validatedWorkflowFields.s3RoleArn}
          helperText="This property stores the AWS S3 Role ARN used for authentication. The database constraint for this property is NVARCHAR(300)."
          label="S3 Role ARN"
          placeholder="Enter the AWS S3 Role ARN"
        />

        <TextInputField
          fieldAtoms={validatedWorkflowFields.s3RoleSessionName}
          helperText="This property stores the AWS S3 Role Session Name used for authentication. The database constraint for this property is NVARCHAR(300)."
          label="S3 Role Session Name"
          placeholder="Enter the AWS S3 Role Session Name"
        />
      </div>

      <div className={styles.formSection}>
        <h3>Notifications and Integration</h3>

        <TextInputField
          fieldAtoms={validatedWorkflowFields.notificationEmails}
          helperText="Enter email addresses that should receive notifications on completion of the product instance. Separate email addresses with commas when there are multiple recipients. The database constraint for this property is NVARCHAR(1000)."
          label="Notification Emails"
          placeholder="Enter email addresses to receive notifications"
        />

        <TextInputField
          fieldAtoms={validatedWorkflowFields.eventParamsMapping}
          helperText="This field is used by the Event Service, where Event Service parameters are mapped to module parameters. The database constraint for this property is NVARCHAR(1000)."
          label="Event Params Mapping"
          placeholder="Enter eventParameterMapping string"
        />

        <TextInputField
          fieldAtoms={validatedWorkflowFields.topicId}
          helperText="This property must be populated when the Event Service is used. It is the mapping between an event request and the submitted orchestration. The CSB submits every orchestration with the given Topic ID. The database constraint for this property is NVARCHAR(300)."
          label="Topic ID"
          placeholder="Enter the Topic ID"
        />

        <TextInputField
          fieldAtoms={validatedWorkflowFields.reportingIndex}
          helperText="The reporting index is used to create a separate index for Elasticsearch when using the Reporting service. The database constraint for this property is NVARCHAR(100)."
          label="Reporting Index"
          placeholder="Enter the reporting index predefined value"
        />

        <TextInputField
          fieldAtoms={validatedWorkflowFields.tenant}
          helperText='This property is an event-specific property used by the Event Service. It is independent of the "worker" property, but is usually the same, e.g. Tenant="wkbe". The database constraint for this property is NVARCHAR(300).'
          label="Tenant"
          placeholder="Enter the tenant name"
        />

        <NumberInputField
          fieldAtoms={validatedWorkflowFields.timeoutAfter}
          helperText="This property specifies the maximum number of hours an Orchestration instance should run, after which it is stopped automatically. The database constraint for this property is INT"
          label="Timeout After nH"
          placeholder="Enter the timeout after n hours value"
        />
      </div>

      <div className={styles.formSection}>
        <h3>Advanced Options</h3>

        <CheckboxInputField
          fieldAtoms={validatedWorkflowFields.unpackInput}
          helperText="Yes = (default value) when we have to unpack the input file for this product instance. No = when we don't have to unpack the input file for this product instance."
          label="Unpack Product"
        />

        <CheckboxInputField
          fieldAtoms={validatedWorkflowFields.supportResubmit}
          helperText='This property indicates when the Orchestration supports resubmit. A developer should modify it after resubmission logic is implemented according to the "CSB Job Resubmit - LLD" low-level design in Confluence. No = (default value) when the workflow does not support resubmission logic. Yes = when the workflow supports resubmission logic.'
          label="Supports Resubmit"
        />

        <CheckboxInputField
          fieldAtoms={validatedWorkflowFields.supportsPrioritization}
          helperText="This property indicates whether the additional popup is displayed when manually submitting an Orchestration instance, allowing the default priority to be overridden. A user can enter the required priority on this screen before submitting the Orchestration instance. No (default) = when we do not need to display a popup where the user can specify the priority on UI submit. Yes = when we need to display a popup where the user can specify the priority on UI submit"
          label="Supports Prioritization"
        />
        <CheckboxInputField
          fieldAtoms={validatedWorkflowFields.strictDropzoneOrdering}
          helperText="This property indicates whether files from the configured dropzone are processed in their defined order. "
          label="Apply Dropzone Ordering"
        />
      </div>
    </div>
  );
};
